import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

export interface TaxRate {
  id: string;
  name: string;
  rate_bp: number;
  is_default: boolean;
  active: boolean;
}

const inputClass =
  "h-8 rounded-md border border-input bg-transparent px-2 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Named tax rates ("GST 5%", "Ontario HST 13%") that fill the tax field on
 * estimates and invoices. Documents keep their own copied rate, so
 * renaming or retiring a rate never changes anything already issued.
 */
export const TaxRatesSettings = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();
  const [name, setName] = useState("");
  const [percent, setPercent] = useState("");

  const { data: rates } = useGetList<TaxRate>("tax_rates", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "name", order: "ASC" },
  });

  const add = () => {
    const pct = Number(percent);
    if (!name.trim()) {
      notify("Give the rate a name", { type: "warning" });
      return;
    }
    if (!(pct >= 0 && pct <= 100)) {
      notify("The rate must be between 0 and 100 percent", {
        type: "warning",
      });
      return;
    }
    create(
      "tax_rates",
      {
        data: {
          workspace_id: getActiveWorkspaceId(),
          name: name.trim(),
          rate_bp: Math.round(pct * 100),
          is_default: (rates ?? []).length === 0,
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          setName("");
          setPercent("");
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error ? error.message : "Could not add the rate",
            { type: "error" },
          ),
      },
    );
  };

  const makeDefault = async (rate: TaxRate) => {
    // Clear the old default first; the partial unique index allows only one.
    const current = (rates ?? []).find((r) => r.is_default);
    if (current && current.id !== rate.id) {
      await new Promise<void>((resolve) =>
        update(
          "tax_rates",
          {
            id: current.id,
            data: { is_default: false },
            previousData: current,
          },
          { mutationMode: "pessimistic", onSettled: () => resolve() },
        ),
      );
    }
    update(
      "tax_rates",
      { id: rate.id, data: { is_default: true }, previousData: rate },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12.5px] text-muted-foreground">
        Named rates fill the tax field on new estimates and invoices. Each
        document copies the number, so changing a rate here never rewrites
        anything already issued.
      </p>

      <div className="flex flex-col gap-1">
        {(rates ?? []).map((rate) => (
          <div
            key={rate.id}
            className="flex flex-wrap items-center gap-2 rounded-md border border-border-subtle px-2 py-1.5 text-[13px]"
          >
            <span className="font-medium">{rate.name}</span>
            <span className="tabular-nums text-muted-foreground">
              {(rate.rate_bp / 100).toFixed(2).replace(/\.00$/, "")}%
            </span>
            {rate.is_default ? (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                Default
              </span>
            ) : (
              <button
                type="button"
                className="text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                onClick={() => void makeDefault(rate)}
              >
                Make default
              </button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto size-7 text-muted-foreground hover:text-destructive"
              onClick={() =>
                deleteOne(
                  "tax_rates",
                  { id: rate.id, previousData: rate },
                  { mutationMode: "pessimistic", onSuccess: () => refresh() },
                )
              }
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}
        {(rates ?? []).length === 0 ? (
          <span className="text-[12.5px] text-muted-foreground">
            No named rates yet; documents use a hand-typed percentage.
          </span>
        ) : null}
      </div>

      <div className="flex items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-[12px] font-medium">
          Name
          <input
            className={inputClass}
            placeholder="Ontario HST"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-medium">
          Rate %
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            className={cn(inputClass, "w-24")}
            placeholder="13"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
        </label>
        <Button size="sm" onClick={add}>
          <Plus className="mr-1 size-3.5" />
          Add
        </Button>
      </div>
    </div>
  );
};

/**
 * Compact picker for document editors: choosing a named rate writes its
 * percentage into the form's tax_rate field via the callback.
 */
export const TaxRatePicker = ({
  onPick,
}: {
  onPick: (percent: number) => void;
}) => {
  const { data: rates } = useGetList<TaxRate>("tax_rates", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "name", order: "ASC" },
    filter: { active: true },
  });
  if (!rates || rates.length === 0) return null;
  return (
    <select
      className="h-8 rounded-md border border-input bg-transparent px-2 text-[12.5px] outline-none"
      value=""
      onChange={(e) => {
        const rate = rates.find((r) => r.id === e.target.value);
        if (rate) onPick(rate.rate_bp / 100);
      }}
    >
      <option value="">Apply a named tax rate…</option>
      {rates.map((rate) => (
        <option key={rate.id} value={rate.id}>
          {rate.name} ({(rate.rate_bp / 100).toFixed(2).replace(/\.00$/, "")}%)
        </option>
      ))}
    </select>
  );
};
